import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminListJournal, adminDeleteJournal } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AdminJournalListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminListJournal()
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteJournal(id);
      toast.success("Post deleted.");
      load();
    } catch (e) {
      toast.error("Could not delete the post.");
    }
  };

  if (loading) return <p className="text-ink-muted font-accent">Loading posts…</p>;

  return (
    <div data-testid="admin-journal-list-page">
      <div className="flex items-center justify-between mb-8">
        <div className="text-sm font-accent text-ink-muted">
          {posts.length} post{posts.length === 1 ? "" : "s"}
        </div>
        <Link
          to="/admin/journal/new"
          data-testid="admin-new-post-link"
          className="inline-flex items-center gap-2 bg-river text-linen px-5 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
        >
          <Plus size={16} strokeWidth={1.5} />
          New post
        </Link>
      </div>

      {posts.length === 0 ? (
        <div className="border border-rule bg-linen-alt p-10 text-center">
          <p className="text-ink-muted">No journal posts yet.</p>
          <Link
            to="/admin/journal/new"
            className="mt-6 inline-flex items-center gap-2 bg-river text-linen px-5 py-3 text-sm font-accent font-semibold"
          >
            Write the first one
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-rule border-y border-rule">
          {posts.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-post-row-${p.id}`}
              className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start"
            >
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-ember font-accent font-semibold">
                    {p.tag}
                  </span>
                  {p.published ? (
                    <span className="inline-flex items-center gap-1 text-xs font-accent text-river">
                      <Eye size={12} strokeWidth={1.5} />
                      Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-accent text-ink-muted">
                      <EyeOff size={12} strokeWidth={1.5} />
                      Draft
                    </span>
                  )}
                </div>
                <Link
                  to={`/admin/journal/${p.id}`}
                  className="font-serif text-xl md:text-2xl text-ink hover:text-river transition-colors tracking-tight leading-tight"
                >
                  {p.title}
                </Link>
                <p className="mt-2 text-sm text-ink-muted line-clamp-2">{p.excerpt}</p>
              </div>
              <div className="md:col-span-3 text-xs text-ink-muted font-accent">
                {formatDate(p.created_at)}
                {p.published && (
                  <div className="mt-1">
                    <Link
                      to={`/journal/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-river hover:underline"
                    >
                      /journal/{p.slug}
                    </Link>
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-2">
                <Link
                  to={`/admin/journal/${p.id}`}
                  data-testid={`admin-edit-post-${p.id}-link`}
                  className="inline-flex items-center gap-1 border border-rule text-ink-muted hover:border-ink hover:text-ink px-3 py-2 text-xs font-accent"
                  aria-label="Edit"
                >
                  <Pencil size={14} strokeWidth={1.5} />
                  Edit
                </Link>
                <button
                  onClick={() => onDelete(p.id, p.title)}
                  data-testid={`admin-delete-post-${p.id}-button`}
                  className="inline-flex items-center gap-1 border border-rule text-ink-muted hover:border-destructive hover:text-destructive px-3 py-2 text-xs font-accent"
                  aria-label="Delete"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
